<!--
  Minimal guest area header: just a "back" link.
  Matches the reduced header from the design demo (account/personal-details).
  Deliberately slim, styled in portal design (neutral/petrol).
-->
<template>
  <header
    ref="header"
    :class="[
      'bg-white border-b sticky top-0 z-30 transition-colors duration-200',
      isStuck ? 'border-neutral-200' : 'border-white'
    ]"
  >
    <div class="container 2xl:max-w-screen-xl mx-auto px-6 py-4">
      <NuxtLink :to="to" class="inline-flex items-center gap-x-2 font-semibold text-neutral-800 hover:text-petrol-500 transition-colors" @click.prevent="goBack">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
          <path d="M15 6l-6 6 6 6" />
        </svg>
        <UiLanguagePlaceholder domain="page.guest" name="return" />
      </NuxtLink>
    </div>
  </header>
</template>

<script setup lang="ts">
const props = defineProps({
  to: {
    type: String,
    default: '/'
  }
})

const router = useRouter()

// Check if a URL belongs to the portal domain or a Travanto domain.
// Comparison with the current host covers local/beta/live automatically
// (e.g. portal.local.travanto.de / *.travanto.de / the live portal domain).
const isTravantoUrl = (url: string): boolean => {
  if (!url) { return false }
  try {
    const host = new URL(url).hostname
    if (host === window.location.hostname) { return true }
    return host === 'travanto.de' || host.endsWith('.travanto.de')
  } catch {
    return false
  }
}

// Back behaviour:
// 1) In-app navigation exists (Vue Router has a predecessor) → go back.
// 2) Direct entry but referrer was a Travanto page → go back anyway.
// 3) Otherwise (no/foreign referrer) → navigate to fallback target (homepage).
const goBack = () => {
  if (window.history.state?.back) {
    router.back()
    return
  }
  if (window.history.length > 1 && isTravantoUrl(document.referrer)) {
    router.back()
    return
  }
  navigateTo(props.to)
}

// The bar is "sticky top-0". While not yet stuck, the bottom border is white
// (invisible). Once scrolled into sticky position (top === 0), the border gets
// its colour (neutral-200). Detected via getBoundingClientRect.
const header = ref<HTMLElement | null>(null)
const isStuck = ref(false)

const updateStuck = () => {
  const el = header.value
  if (!el) { return }
  isStuck.value = el.getBoundingClientRect().top <= 0
}

onMounted(() => {
  updateStuck()
  window.addEventListener('scroll', updateStuck, { passive: true })
  window.addEventListener('resize', updateStuck)
})
onBeforeUnmount(() => {
  window.removeEventListener('scroll', updateStuck)
  window.removeEventListener('resize', updateStuck)
})
</script>
